import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { supabase } from './services/supabase'

// Context
import { AppProvider, useApp } from './context/AppContext'

// Components
import { AuthScreen } from './components/auth/AuthScreen'
import { Layout } from './components/Layout'
import { Modals } from './components/Modals'

// Pages
import { AddReadingPage, MetersPage, SettingsPage } from './pages'

// ============================================================================
// AUTH HOOK
// ============================================================================

function useAuth() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  return { session, setSession, loading }
}

// ============================================================================
// APP CONTENT (inside context)
// ============================================================================

function AppContent() {
  const { loading } = useApp()
  const navigate = useNavigate()

  // Always open to Add Reading when app resumes from background (iOS PWA)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && window.location.pathname !== '/') {
        navigate('/', { replace: true })
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-slate-200 border-t-sky-700 rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<AddReadingPage />} />
          <Route path="/meters" element={<MetersPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
      <Modals />
    </>
  )
}

// ============================================================================
// MAIN APP
// ============================================================================

function App() {
  const { session, setSession, loading } = useAuth()
  const [pendingInviteCode] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('invite') || null
  })

  const redeemInviteCode = async (userId, code) => {
    const { data: invites, error: inviteErr } = await supabase
      .from("operation_invites")
      .select("operation_id")
      .eq("invite_code", code)
      .single()

    if (inviteErr || !invites) throw new Error('Invalid or expired invite code')

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error("Could not retrieve user email")

    const { data: existing } = await supabase
      .from("operation_members")
      .select("id")
      .eq("operation_id", invites.operation_id)
      .eq("user_id", userId)
      .maybeSingle()

    if (existing) return true

    const { error: memberErr } = await supabase
      .from("operation_members")
      .insert([{ operation_id: invites.operation_id, user_id: userId, email: user.email }])

    if (memberErr) throw memberErr
    return true
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-slate-200 border-t-sky-700 rounded-full animate-spin" />
      </div>
    )
  }

  if (!session) {
    return (
      <AuthScreen
        onAuthed={setSession}
        pendingInviteCode={pendingInviteCode}
        redeemInviteCode={redeemInviteCode}
      />
    )
  }

  return (
    <BrowserRouter>
      <AppProvider session={session} setSession={setSession}>
        <AppContent />
      </AppProvider>
    </BrowserRouter>
  )
}

export default App
